import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import type { Organization, Site } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, Input, Select, Textarea } from '../../components/ui'
import { Wizard } from '../../components/Wizard'

const MINERALS = [
  'Gold', 'Silver', 'Diamond', 'Copper', 'Bauxite', 'Iron Ore',
  'Manganese', 'Chromite', 'Coltan', 'Lithium', 'Cobalt', 'Other',
]
const LAND_TENURE_TYPES = ['informal', 'customary', 'leasehold', 'freehold', 'state']

export default function SiteRegister() {
  const navigate = useNavigate()
  const [minerals, setMinerals] = useState<string[]>([])

  const orgsQuery = useQuery({
    queryKey: ['gov', 'orgs-for-site'],
    queryFn: () => api.get<{ organizations: Organization[] }>('/organizations?limit=300'),
  })

  const register = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ site: Site }>('/gov/sites', body),
    onSuccess: (res) => navigate(`/sites/${res.site.id}`),
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const lat = form.get('latitude')
    const lng = form.get('longitude')
    const area = form.get('area_size_ha')
    register.mutate({
      name: String(form.get('name') ?? '').trim(),
      org_id: String(form.get('org_id') ?? ''),
      mineral_types: minerals,
      land_tenure_type: String(form.get('land_tenure_type') ?? 'informal'),
      region_id: String(form.get('region_id') ?? '').trim(),
      district_id: String(form.get('district_id') ?? '').trim(),
      description: String(form.get('description') ?? '').trim(),
      latitude: lat ? Number(lat) : undefined,
      longitude: lng ? Number(lng) : undefined,
      area_size_ha: area ? Number(area) : undefined,
    })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Register a mining site</h1>
          <p className="page-head__sub">On behalf of an organisation, or unassigned.</p>
        </div>
      </div>

      <Card>
        <CardHeader title="Site details" />
        <CardBody>
          <form onSubmit={onSubmit}>
            <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              {register.isError && (
                <Alert tone="danger">
                  {register.error instanceof ApiError ? register.error.message : 'Could not register that site.'}
                </Alert>
              )}

              {/* PWA: guided steps (max ~3 fields each); desktop stacked, unchanged. */}
              <Wizard
                steps={[
                  { title: 'Site & organisation', content: (
                    <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              <Input label="Site name" name="name" required />

              <Select label="Organisation (optional)" name="org_id" defaultValue="">
                <option value="">Unassigned</option>
                {(orgsQuery.data?.organizations ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </Select>
                    </div>
                  ) },
                  { title: 'Minerals & tenure', content: (
                    <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              <div>
                <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>Minerals</label>
                <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                  {MINERALS.map((m) => (
                    <label key={m} className={`radio-opt${minerals.includes(m) ? ' radio-opt--checked' : ''}`}
                           style={{ padding: 'var(--sp-2) var(--sp-3)' }}>
                      <input
                        type="checkbox"
                        checked={minerals.includes(m)}
                        onChange={() => setMinerals((cur) => (
                          cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]
                        ))}
                      />
                      {m}
                    </label>
                  ))}
                </div>
              </div>

              <Select label="Land tenure" name="land_tenure_type" defaultValue="informal">
                {LAND_TENURE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
                    </div>
                  ) },
                  { title: 'Location', content: (
              <div className="grid grid--2">
                <Input label="Province / region" name="region_id" required />
                <Input label="District" name="district_id" />
              </div>
                  ) },
                  { title: 'Coordinates & size', content: (
                    <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              <div className="grid grid--2">
                <Input label="Latitude (optional)" name="latitude" type="number" step="any" />
                <Input label="Longitude (optional)" name="longitude" type="number" step="any" />
              </div>
              <Input label="Area (hectares, optional)" name="area_size_ha" type="number" step="any" min="0" />
                    </div>
                  ) },
                  { title: 'Description', content: (
              <Textarea label="Description (optional)" name="description" rows={3} />
                  ) },
                ]}
                footer={(
              <div className="row">
                <Button type="submit" loading={register.isPending} confirm={{ title: "Register this site?", message: "It will be submitted for inspection." }}>Register site</Button>
              </div>
                )}
              />
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  )
}
